import { epochSecondsToDate, z, type ZodType } from '@customerio/hydra-validation';
import type { GeosetsFenceModel, GeosetsGeosetModel } from '@customerio/hydra-ui-api';
import { defineEntity } from '../shared/define-entity';

/**
 * Geoset entity — workspace-scoped named collections of geofences that
 * campaigns and segments target ("a single geofence is a set of one").
 *
 * The wire id is numeric, but this data layer normalizes ids to strings
 * so routes, query keys, and future FK-following helpers all share the
 * same id shape — matching the geofence entity it sits beside.
 */

/* ------------------------------------------------------------------------- */
/* Type                                                                       */
/* ------------------------------------------------------------------------- */

export type Geoset = Omit<GeosetsGeosetModel, 'id' | 'created_at' | 'updated_at'> & {
  id: string;
  created_at: Date;
  updated_at: Date;
};

/* ------------------------------------------------------------------------- */
/* Schemas — only the transforms                                              */
/* ------------------------------------------------------------------------- */

const GeosetFromApi = z.looseObject({
  id: z.number().transform(String),
  created_at: epochSecondsToDate,
  updated_at: epochSecondsToDate,
}) as unknown as ZodType<Geoset, unknown>;

/* ------------------------------------------------------------------------- */
/* List params                                                                */
/* ------------------------------------------------------------------------- */

/**
 * The list endpoint (`GET …/geosets`) takes no query params today — it
 * returns every geoset in the workspace in one unpaginated response. The
 * type is kept as an (empty) params object so the shared key factory and a
 * future filtered list share the same shape as the other entities.
 */
export type GeosetListParams = Record<string, never>;

/* ------------------------------------------------------------------------- */
/* Entity                                                                     */
/* ------------------------------------------------------------------------- */

/** @public */
export const geoset = defineEntity<Geoset, never, GeosetListParams, undefined, 'workspace'>({
  name: 'geoset',
  scope: 'workspace',
  pk: (g) => g.id,
  schema: { read: GeosetFromApi },
});

export const geosetKeys = geoset.keys;

export function parseGeoset(raw: GeosetsGeosetModel): Geoset {
  return geoset.parse(raw);
}

/* ------------------------------------------------------------------------- */
/* Geoset fence — the membership read model                                   */
/* ------------------------------------------------------------------------- */

/**
 * A fence as returned by `geosets.getFences`. Carries the same data as a
 * workspace `Geofence`, with the free-form `attributes` standing in for the
 * geofence's `metadata` (address / saved radius unit). The geoset detail page
 * adapts these directly (see `geosetFenceToGeofence`) — no enrichment read
 * against the `/geofences` list endpoint is needed.
 */
export type GeosetFence = Omit<GeosetsFenceModel, 'id' | 'created_at' | 'updated_at'> & {
  id: string;
  created_at: Date;
  updated_at: Date;
};

const GeosetFenceFromApi = z.looseObject({
  id: z.number().transform(String),
  created_at: epochSecondsToDate,
  updated_at: epochSecondsToDate,
}) as unknown as ZodType<GeosetFence, unknown>;

export function parseGeosetFence(raw: GeosetsFenceModel): GeosetFence {
  return GeosetFenceFromApi.parse(raw);
}
